import type { AddressSchemaType } from 'src/components/checkout/steps/shipping/components/Address/AddressForm'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import { unmaskCEP } from 'src/utils/formatCEP'
import { addressService } from 'src/services/addressService'

const PRE_FILLED_GATEWAY_FIELDS = [
  'street',
  'city',
  'state',
  'neighborhood',
] as const

export const OPTIONAL_ADDRESS_FIELDS = [
  { name: 'street', label: 'Rua / Logradouro' },
  { name: 'city', label: 'Cidade' },
  { name: 'state', label: 'UF' },
  { name: 'neighborhood', label: 'Bairro' },
]

export function buildPostalInputDisabledFlags(
  data: Partial<AddressData> | null,
  editableFields: string[]
) {
  const lockWhenFilled = (field: typeof PRE_FILLED_GATEWAY_FIELDS[number]) =>
    Boolean(data?.[field]) && !editableFields.includes(field)

  return {
    street: lockWhenFilled('street'),
    city: lockWhenFilled('city'),
    state: lockWhenFilled('state'),
    neighborhood: lockWhenFilled('neighborhood'),
    number: !editableFields.includes('number'),
    complement: !editableFields.includes('complement'),
    receiverName: !editableFields.includes('receiverName'),
    reference: !editableFields.includes('reference'),
  }
}

export function buildCheckoutAddressFromFormValues(
  formValues: AddressSchemaType,
  initialData: Partial<AddressData> | null
) {
  return {
    addressType: 'residential' as 'residential' | 'commercial',
    receiverName: formValues.receiverName,
    postalCode: unmaskCEP(initialData?.postalCode ?? ''),
    city: formValues.city ?? initialData?.city ?? '',
    state: formValues.state ?? initialData?.state ?? '',
    country: 'BRA',
    street: formValues.street ?? initialData?.street ?? '',
    number: formValues.number,
    neighborhood: formValues.neighborhood ?? initialData?.neighborhood ?? '',
    complement: formValues.complement,
    addressId: initialData?.addressId ?? '',
    geoCoordinates: [],
    isDisposable: true,
    reference: formValues.reference ?? 'Casa',
  }
}

export async function persistCheckoutAddress(payload: {
  orderFormId: string
  address: ReturnType<typeof buildCheckoutAddressFromFormValues>
  setOrderForm: (orderForm: OrderForm) => void
}) {
  const { address, orderFormId, setOrderForm } = payload

  if (address.addressId) {
    return addressService.updateAddress({
      orderFormId,
      addressId: address.addressId,
      address,
      syncOrderFormFn: setOrderForm,
    })
  }

  return addressService.createAddress({
    orderFormId,
    address,
    syncOrderFormFn: setOrderForm,
  })
}
